import { activityTracker, type ErrorClient } from "./activityTracker";
import { buildEndpointKey } from "./endpointKey";

/**
 * Errores de las llamadas HTTP de la app para Métricas de Actividad. Solo CÓDIGOS CERRADOS y datos sanitizados:
 * nunca el mensaje, la URL ni el cuerpo (podrían traer datos de pacientes). El detalle que viaja es el status,
 * una plantilla del endpoint SIN ids ni query (`utils/endpointKey.ts`), a qué backend le pegó, cuánto tardó y si
 * ya se había reintentado.
 *
 * Qué cuenta como error (y qué no): los fallos de INFRAESTRUCTURA — timeout, red caída, 5xx y 401/403
 * (sesión/permisos). Los 4xx de negocio (400 datos inválidos, 404, 409, 422, 429) son respuestas
 * esperadas de un formulario o de una regla, no un problema de la plataforma, y NO se cuentan.
 *
 * Los tres clientes (`http`, `secureHttp`, `beHttp`) llaman acá. El transporte de la propia captura
 * usa `fetch` directo, así que un fallo suyo no se cuenta (evita un bucle). `trackError` agrupa las
 * repeticiones y nunca tira.
 */

/** Lo que el cliente HTTP sabe de la llamada. */
export interface RequestContext {
  client: ErrorClient;
  method?: string;
  url: string;
  /** `Date.now()` al arrancar el intento que produjo la respuesta o el fallo. */
  startedAt: number;
  /** El intento no es el primero. */
  retried: boolean;
  /** Id que se mandó en `x-request-id` (solo las llamadas a psiplatform-be; a api-core no se le manda). */
  requestId?: string;
}

// La respuesta y el parseo están en funciones distintas de cada cliente: el contexto viaja pegado a la Response.
const contexts = new WeakMap<Response, RequestContext>();

export function rememberRequest(resp: Response, ctx: RequestContext): void {
  try {
    contexts.set(resp, ctx);
  } catch {
    /* nunca tira */
  }
}

function detailOf(ctx: RequestContext | undefined) {
  if (!ctx) return undefined;
  const endpointKey = buildEndpointKey(ctx.method, ctx.url);
  return {
    client: ctx.client,
    ...(endpointKey ? { endpointKey } : {}),
    durationMs: Math.max(0, Date.now() - ctx.startedAt),
    retried: ctx.retried,
    ...(ctx.requestId ? { requestId: ctx.requestId } : {}),
  };
}

export function trackNetworkFailure(kind: "timeout" | "network_error", ctx?: RequestContext): void {
  activityTracker.trackError(kind, detailOf(ctx));
}

export function trackHttpStatus(status: number, resp?: Response): void {
  const detail = { ...(resp ? detailOf(contexts.get(resp)) : undefined), httpStatus: status };
  if (status === 401 || status === 403) activityTracker.trackError("auth_error", detail);
  else if (status >= 500) activityTracker.trackError("api_error", detail);
}
